import { Directive, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  DataNoRowOverlayComponent,
  DataNoRowOverlayParams,
  GroupDistributionAgg,
  HeaderWithIconTemplate,
  LoadingCellRendererComponent,
  StarredCellRendererComponent,
} from '@y42/ag-grid';
import { ConfirmationDialogModel, ConfirmDialogComponent } from '@y42/core/dialog/confirm-dialog';
import { ListViewColDef, ListViewComponent, QuickFilterModel } from '@y42/core/list-view';
import { DateTimeService } from '@y42/date-time';
import { Y42FeatureType } from '@y42/json-schema';
import {
  GetContextMenuItems,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridOptions,
  MenuItemDef,
} from 'ag-grid-community';
import merge from 'lodash/merge';
import { filter, tap } from 'rxjs';
import { FeatureAggregationService } from '../../api';
import { DetailTabModel, DETAIL_TABS } from '../../detail-view/feature-detail-tab.model';
import { agGridLastUsedIcon } from '../../icons';
import { UserRelatedData } from '../../models';
import { Y42FeatureTypeToRoute, Y42RouteDetailTab, Y42RouteDetailTabs, Y42RouterService } from '../../routing';
import { FeatureListViewData, FeatureListViewDataWithChild } from './abstract-feature-list-view.service.model';
import {
  EntryGroupCellRendererComponent,
  EntryGroupCellRendererParams,
} from './entry-group-cell-renderer/entry-group-cell-renderer.component';
import { getFeatureListViewRowId } from './get-feature-list-view-row-id.util';
import { TagsCellRendererComponent } from './tags-cell-renderer/tags-cell-renderer.component';

@UntilDestroy()
@Directive({ selector: 'y42-list-view[y42FeatureListView]' })
export class FeatureListViewDirective<D extends FeatureListViewData> implements OnInit, OnChanges {
  constructor(
    private readonly listViewComponent: ListViewComponent,

    private readonly dateTimeService: DateTimeService,
    private readonly routerService: Y42RouterService,

    private readonly featureAggregationService: FeatureAggregationService,
    private readonly dialog: MatDialog,
    @Optional() @Inject(DETAIL_TABS) private readonly tabs?: DetailTabModel[],
  ) {}

  @Input() id!: Y42FeatureType;
  @Input() defaultQuickFilters: QuickFilterModel[] = [];
  @Input() gridOptions: GridOptions = {};
  @Input() columnDefs: ListViewColDef[] = [];
  @Input() frameworkComponents: GridOptions['components'] = {};
  @Input() additionalContextMenuActions?: (params: GetContextMenuItemsParams) => ReturnType<GetContextMenuItems>;

  ngOnInit() {
    this.setOptions();
    this.setColumns();
    this.setFrameworkComponents();
    this.setDefaultQuickFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.gridOptions) {
      this.setOptions();
    }

    if (changes.columnDefs) {
      this.setColumns();
    }

    if (changes.frameworkComponents) {
      this.setFrameworkComponents();
    }

    if (changes.defaultQuickFilters) {
      this.setDefaultQuickFilters();
    }
  }

  private setOptions() {
    this.listViewComponent.gridOptions = merge(
      this.getDefaultGridOptions({
        getContextMenuItemsFn: (params) => this.getContextMenuItems(params),
      }),
      this.listViewComponent.gridOptions,
    );
  }

  private setColumns() {
    const defaultColWhithoutOverwrite = this.getDefaultColDefs().filter((defaultCol) =>
      this.columnDefs.every((currColumn) => currColumn.field !== defaultCol.field),
    );
    const colDefs = [...defaultColWhithoutOverwrite, ...(this.columnDefs || [])]
      .map((col) => ({ ...col }))
      .sort((colA, colB) => (colA.order || 0) - (colB.order || 0));
    colDefs.forEach((colDef) => delete colDef.order);

    this.listViewComponent.columnDefs = colDefs;
  }

  private setFrameworkComponents() {
    this.listViewComponent.frameworkComponents = {
      ...this.getDefaultFrameworkComponents(),
      ...(this.frameworkComponents || {}),
    };
  }

  private setDefaultQuickFilters() {
    this.listViewComponent.defaultQuickFilters = [
      ...this.getDefaultQuickFilters(),
      ...(this.defaultQuickFilters || []),
    ];
  }

  private getContextMenuItems(params: GetContextMenuItemsParams): ReturnType<GetContextMenuItems> {
    const defaultActions: ReturnType<GetContextMenuItems> = ['copy', 'export', 'autoSizeAll'];

    if (!params.node) {
      return defaultActions;
    }

    const $path = getFeatureListViewRowId(params.node);

    if (!$path) {
      return defaultActions;
    }

    const { service, query } = this.featureAggregationService.getServiceDefinitionByPath($path)!;
    const item = query.getUIEntity($path);

    const deleteAction: MenuItemDef = {
      name: item ? `Delete ${item.name}` : 'Delete',
      action: () =>
        this.dialog
          .open(ConfirmDialogComponent, {
            width: '600px',
            panelClass: ['confirmation-pop-up'],
            data: {
              header_text: `Delete ${item?.featureType}`,
              text: `Are you sure you want to delete ${item ? `"${item.name}"` : 'this item'}`,
              data: true,
            } as ConfirmationDialogModel,
          })
          .afterClosed()
          .pipe(
            filter(Boolean),
            tap(() => service.delete($path)),
          )
          .subscribe(),
    };

    if (!params.node?.group) {
      return [deleteAction, ...defaultActions];
    }

    const additionalActions: ReturnType<GetContextMenuItems> = this.additionalContextMenuActions?.(params) ?? [];

    return [
      {
        name: 'Open',
        subMenu: this.getGoToSubMenus(params, false),
      },
      {
        name: 'Open in new tab',
        subMenu: this.getGoToSubMenus(params, true),
      },
      deleteAction,
      ...additionalActions,
      ...defaultActions,
    ];
  }

  private getGoToSubMenus(params: GetContextMenuItemsParams, openInNewTab: boolean): MenuItemDef[] | undefined {
    if (!this.tabs?.length) {
      return [];
    }

    const data: D = params.node!.group ? params.node!.allLeafChildren[0]?.data : params.node!.data;

    return this.tabs.map((tab: DetailTabModel) => ({
      name: tab.label,
      icon: `<img style="max-width: none;" width="15" src="${tab.logo}">`,
      action: () => this.openProperty(data, openInNewTab, tab.path),
    }));
  }

  private openProperty(data: D, isNewTab: boolean, detail: Y42RouteDetailTab = Y42RouteDetailTabs.Default) {
    const route = Y42FeatureTypeToRoute[data.featureType];

    if (isNewTab) {
      const url = this.routerService.getURLToDetail(route, data.id.split('/').pop() || '', detail);
      window.open(url, '_blank');
    } else {
      this.routerService.navigateToDetail(route, data.id.split('/').pop() || '', detail);
    }
  }

  private getDefaultQuickFilters(): QuickFilterModel[] {
    return [
      {
        id: 'starred',
        default: false,
        name: 'Starred',
        editable: false,
        filterModel: {
          starred: {
            values: ['true'],
            type: 'equals',
          },
        },
        is_font_awesome: true,
        icon: 'star',
        background_color: '#fcfcfc',
      },
      {
        id: 'last-used',
        default: false,
        name: 'Used in the last 7 days',
        editable: false,
        filterModel: {
          last_used: {
            type: 'greaterThan',
            values: [this.dateTimeService.subtract(new Date(), { days: 7 }), ''],
          },
        },
        is_font_awesome: true,
        icon: 'history',
        background_color: '#fcfcfc',
      },
    ];
  }

  private getDefaultGridOptions(opt?: {
    hideDetails?: boolean;
    noRowsMessage?: DataNoRowOverlayParams;
    getContextMenuItemsFn?: GetContextMenuItems;
  }): GridOptions {
    return {
      rowBuffer: 10,
      suppressAggFuncInHeader: true,
      suppressCellFocus: true,
      suppressColumnVirtualisation: true,
      enableGroupEdit: true,
      sideBar: 'filters',
      groupDisplayType: 'multipleColumns',
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMovable: true,
        flex: 1,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        filterParams: {
          suppressAndOrCondition: true,
          includeBlanksInLessThan: true,
          trimInput: false,
        },
      },
      getRowId: ({ data }: GetRowIdParams<FeatureListViewDataWithChild>) => `${data.id}|${data.child_$path}`,
      loadingCellRenderer: 'loadingRenderer',
      autoGroupColumnDef: {
        headerName: 'Name',
        cellRenderer: 'entryGroup',
        cellRendererParams: {
          showChildElements: false,
          hideDetails: opt?.hideDetails,
        } satisfies EntryGroupCellRendererParams,
        minWidth: 220,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        suppressSizeToFit: true,
        aggFunc: 'first',
        filter: 'agTextColumnFilter',
        field: 'name',
        pinned: 'left',
        comparator: (valueA, valueB, nodeA, nodeB) => {
          if (nodeA.group && nodeB.group) {
            const valA = nodeA.allLeafChildren[0]?.data.name.toLowerCase();
            const valB = nodeB.allLeafChildren[0]?.data.name.toLowerCase();
            if (valA < valB) {
              return -1;
            }
            if (valA > valB) {
              return 1;
            }
          }
          return 0;
        },
      },
      noRowsOverlayComponent: 'dataNoRowOverlayComponent',
      noRowsOverlayComponentParams: opt?.noRowsMessage as DataNoRowOverlayParams,
      rowClass: 'cursor-pointer',
      groupHideOpenParents: false,
      animateRows: true,
      overlayNoRowsTemplate: 'agGridNoRowsComponent',
      getContextMenuItems: (params) => opt?.getContextMenuItemsFn?.(params) || [],
      aggFuncs: {
        tag: (params) => {
          const uniqueValues = Array.from(new Set(params.values.flat()));
          return uniqueValues;
        },
        table: (params) => {
          if (params.values.length === 1) {
            if (!params.rowNode.allLeafChildren[0]?.data.last_import_date) {
              return 0;
            } else {
              return 1;
            }
          } else {
            return params.values.length;
          }
        },
        distribution: GroupDistributionAgg,
      },
    };
  }

  private getDefaultColDefs() {
    const listViewBaseToColDef: Record<'last_used', ListViewColDef> = {
      last_used: {
        headerName: 'Last Used',
        field: 'last_used',
        colId: 'last_used',
        aggFunc: 'first',
        filter: 'dateFilter',
        minWidth: 100,
        maxWidth: 150,
        headerComponentParams: {
          template: HeaderWithIconTemplate(agGridLastUsedIcon),
        },
        sort: 'desc',
        sortIndex: 1,
        valueFormatter: (params) => {
          const last_used: UserRelatedData['last_used'] = params.value;
          return params?.node?.group
            ? last_used
              ? this.dateTimeService.formatDateRelativeToNow(new Date(last_used))
              : '-'
            : '';
        },
        order: 60,
      },
    };
    return Object.values(listViewBaseToColDef);
  }

  private getDefaultFrameworkComponents() {
    return {
      entryGroup: EntryGroupCellRendererComponent,
      loading: LoadingCellRendererComponent,
      dataNoRowOverlayComponent: DataNoRowOverlayComponent,
      tags: TagsCellRendererComponent,
      starred: StarredCellRendererComponent,
    };
  }
}
